import { Component, Element, Host, Listen, Prop, Watch, h } from '@stencil/core';
import debounce from 'debounce';
import { getDocumentHeight, isMagnoliaEditingMode } from '../../../helper';

export interface PageDimension {
  scrollTopBottomOfScreen: number;
  heightOfDocumentWithoutFooter: number;
}

@Component({
  tag: 'ui-cms-sticky-toolbar',
  styleUrls: { default: 'ui-cms-sticky-toolbar.scss', editing: 'ui-cms-sticky-toolbar.magnolia.scss' },
  shadow: true,
})
export class UiCmsStickyToolbar {
  @Element() el: HTMLElement;

  @Prop({ mutable: true }) items: (
    | HTMLUiCmsStickyToolbarItemSocialFollowLinksElement
    | HTMLUiCmsStickyToolbarItemStandardElement
  )[];

  @Prop({ mutable: true }) i18n = {
    navigationName: 'Seitennavigation',
  };

  @Prop({ attribute: 'i18n' }) utilityI18n: string;

  @Watch('utilityI18n')
  utilityI18nChangeHandler(newValue: string) {
    if (newValue) this.i18n = { ...this.i18n, ...JSON.parse(newValue) };
  }

  minTopPosition = 200;

  pageContentSelector = '.shadow-wrapper';

  footerSelector = '#footer';

  headerSelector = '#header';

  stageSelector = '.stage';

  lastTopPosition: number | 'auto';

  lastBottomPosition: number | 'auto';

  @Watch('items')
  onItemsChange() {
    if (this.items.length > 0) {
      this.setItemsMinHeight();
    }
  }

  @Listen('itemInteraction')
  itemInteractionHandler(e) {
    this.items.forEach((item) => {
      if (e.detail.el !== item) {
        item.hideContent();
      } else {
        item.showContent(this.lastTopPosition);
        this.setItemsMinHeight(); // Needed for IPad Chrome
      }
    });
  }

  @Listen('click', { target: 'window' })
  checkForClickOutside(ev) {
    if (!this.el.contains(ev.target)) {
      this.items.forEach((item) => {
        item.hideContent();
      });
    }
  }

  @Listen('itemClick')
  itemClickHandler(e) {
    this.items.forEach((item) => {
      item.hideContent();
      if (e.detail.el === item) {
        item.showContent(this.lastTopPosition);
      }
    });
  }

  componentWillLoad() {
    const socialFollowLinksItems = [].slice.call(
      this.el.querySelectorAll('ui-cms-sticky-toolbar-item-social-follow-links')
    );
    const standardItems = [].slice.call(this.el.querySelectorAll('ui-cms-sticky-toolbar-item-standard'));
    this.items = [...socialFollowLinksItems, ...standardItems];
    this.utilityI18nChangeHandler(this.utilityI18n);
  }

  componentDidLoad() {
    if (document.querySelector(this.stageSelector) || document.querySelector(this.headerSelector)) {
      const setToolbarPositionDebounced = debounce(this.setToolbarPosition.bind(this), 200);
      const observer = new ResizeObserver(() => {
        setToolbarPositionDebounced();
      });

      if (document.querySelector(this.stageSelector)) {
        observer.observe(document.querySelector(this.stageSelector));
      }
      if (document.querySelector(this.headerSelector)) {
        observer.observe(document.querySelector(this.headerSelector));
      }
    } else {
      this.setToolbarPosition();
    }

    window.addEventListener('scroll', this.setToolbarPosition.bind(this));
  }

  getToolbarTopPosition(shadowWrapperHeight) {
    const pageDimensions = this.getPageDimensions();
    let top = window.innerHeight / 2 - this.el.offsetHeight / 2 - window.pageYOffset;

    if (document.querySelector(this.stageSelector)) {
      const stage = document.querySelector(this.stageSelector) as HTMLElement;
      top = stage.offsetHeight + 34 - window.pageYOffset;
    }

    if (document.querySelector(this.headerSelector)) {
      const header = document.querySelector(this.headerSelector) as HTMLElement;
      top = header.offsetHeight + 34 - window.pageYOffset;
    }

    if (document.querySelector(this.stageSelector) && document.querySelector(this.headerSelector)) {
      const stage = document.querySelector(this.stageSelector) as HTMLElement;
      const header = document.querySelector(this.headerSelector) as HTMLElement;
      top = header.offsetHeight + stage.offsetHeight + 34 - window.pageYOffset;
    }

    if (
      pageDimensions.scrollTopBottomOfScreen >= pageDimensions.heightOfDocumentWithoutFooter &&
      shadowWrapperHeight > 400
    ) {
      return 'auto';
    }
    return top < this.minTopPosition ? this.minTopPosition : top;
  }

  getToolbarBottomPosition(shadowWrapperHeight) {
    const pageDimensions = this.getPageDimensions();

    const bottom = window.pageYOffset + window.innerHeight - shadowWrapperHeight + 130;

    if (pageDimensions.scrollTopBottomOfScreen >= pageDimensions.heightOfDocumentWithoutFooter) {
      const minBottomPosition = window.innerHeight - this.el.offsetHeight - this.minTopPosition;
      return bottom > minBottomPosition ? bottom : minBottomPosition;
    }
    return 'auto';
  }

  getPageDimensions(): PageDimension {
    const footerHeight = document.querySelector(this.footerSelector)
      ? (document.querySelector(this.footerSelector) as HTMLElement).offsetHeight
      : 0;

    return {
      scrollTopBottomOfScreen: window.pageYOffset + window.innerHeight,
      heightOfDocumentWithoutFooter: getDocumentHeight() - footerHeight,
    };
  }

  setToolbarPosition() {
    const shadowWrapperHeight = document.querySelector(this.pageContentSelector)
      ? (document.querySelector(this.pageContentSelector) as HTMLElement).offsetHeight
      : document.body.offsetHeight;
    const top = this.getToolbarTopPosition(shadowWrapperHeight);

    const bottom = this.getToolbarBottomPosition(shadowWrapperHeight);

    if (top !== this.lastTopPosition) {
      this.lastTopPosition = top;
      this.el.style.top = this.lastTopPosition !== 'auto' ? `${this.lastTopPosition}px` : this.lastTopPosition;
    }
    if (bottom !== this.lastBottomPosition) {
      this.lastBottomPosition = bottom;
      this.el.style.bottom =
        this.lastBottomPosition !== 'auto' ? `${this.lastBottomPosition}px` : this.lastBottomPosition;
    }
  }

  getToolBarHeight() {
    return this.el.offsetHeight;
  }

  setItemsMinHeight() {
    if (!isMagnoliaEditingMode()) {
      this.items.forEach((item) => {
        // eslint-disable-next-line no-param-reassign
        item.minHeight = this.getToolBarHeight();
      });
    }
  }

  render() {
    return (
      <Host>
        <nav aria-label={this.i18n.navigationName}>
          <slot></slot>
        </nav>
      </Host>
    );
  }
}
